import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/EditOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ReactFlow, {
  Controls,
  Background,
  Panel,
  useNodesState,
  useEdgesState,
  MarkerType,
  Node,
  Edge,
  OnNodesChange,
  OnEdgesChange,
} from "reactflow";
import WorkflowNode from "./WorkflowNode.tsx";
import {
  getLayoutedElements,
  loadWorkflowNodesWithTriggers,
} from "./workflowUtils.ts";
import { refineWorkflow } from "../../api/agentApi.js";

const nodeTypes = { muiNode: WorkflowNode };

interface RefinementMessage {
  id: string;
  text: string;
  timestamp: Date;
  type: "user" | "agent";
}

interface WorkflowReviewProps {
  workflowName: string;
  onWorkflowNameChange?: (name: string) => void;
  dagDefinition: any;
  isEdit: boolean;
  isCreating: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  intent: string;
  onIntentChange?: (newIntent: string) => void;
  /** Intent history from the generate step — seeded into accumulatedIntentHistory so refinements append to it. */
  initialIntentHistory?: any[] | null;
  /** Called when the refine API returns an updated DAG. Parent should update dagDefinition. */
  onDagRefined?: (updatedDag: any, updatedName: string, intentHistory?: any[] | null) => void;
  /**
   * Optional override for refinement execution. When provided, the editor calls this
   * instead of the stateless refineWorkflow() API. Useful in edit mode to call the
   * in-place PUT /workflow/{id}/refine endpoint that saves as a new version immediately.
   * Should return { updatedDag, updatedName } on success, or throw on failure.
   */
  onRefinementRequested?: (refinementText: string) => Promise<{ updatedDag: any; updatedName: string }>;
  /**
   * Edit mode only: called when the user explicitly saves a name change.
   * Parent should persist the new name (e.g. via PUT /workflow/{id}).
   */
  onWorkflowNameSave?: (newName: string) => Promise<void>;
}

const WorkflowRefinementEditor: React.FC<WorkflowReviewProps> = ({
  workflowName,
  onWorkflowNameChange,
  dagDefinition,
  isEdit,
  isCreating,
  onConfirm,
  onCancel,
  intent,
  onIntentChange,
  initialIntentHistory,
  onDagRefined,
  onRefinementRequested,
  onWorkflowNameSave,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [showDataMappings, setShowDataMappings] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  // Seeded from generate-phase history; grows with each pre-save refinement turn
  const [accumulatedIntentHistory, setAccumulatedIntentHistory] = useState<any[] | null>(
    initialIntentHistory ?? null,
  );
  // Inline-edit state for the workflow name
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInputValue, setNameInputValue] = useState(workflowName);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [chatHistory, setChatHistory] = useState<RefinementMessage[]>(
    // Don't seed an empty bubble — only add the initial message if there's actual intent text
    intent.trim()
      ? [{ id: "initial", text: intent, timestamp: new Date(), type: "user" }]
      : [],
  );
  const [inputValue, setInputValue] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Detect PLACEHOLDER or missing agent_ids in the current DAG — shown as a warning banner
  const placeholderSteps = useMemo(() => {
    const steps: any[] = dagDefinition?.steps ?? [];
    return steps
      .filter(
        (s) =>
          s.type === "agent" &&
          (!(s.agent_id ?? "").trim() || (s.agent_id ?? "").toUpperCase() === "PLACEHOLDER")
      )
      .map((s) => s.id as string);
  }, [dagDefinition]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleSendRefinement = async () => {
    if (!inputValue.trim() || isRefining) return;

    const refinementText = inputValue.trim();
    const userMessage: RefinementMessage = {
      id: Date.now().toString(),
      text: refinementText,
      timestamp: new Date(),
      type: "user",
    };
    setChatHistory((prev) => [...prev, userMessage]);
    setInputValue("");

    if (onDagRefined) {
      setIsRefining(true);
      try {
        let updatedDag: any;
        let updatedName: string;
        let freshHistory: any[] | null = null;

        if (onRefinementRequested) {
          // Delegate to parent (e.g. in-place save via PUT /workflow/{id}/refine)
          const result = await onRefinementRequested(refinementText);
          updatedDag = result.updatedDag;
          updatedName = result.updatedName;
        } else {
          // Stateless refine (new workflow creation flow)
          // Pass accumulated history so the backend appends each turn correctly
          const result = await refineWorkflow(intent, refinementText, dagDefinition, 20, accumulatedIntentHistory as any);
          updatedDag = result?.dag_definition ?? result;
          updatedName = result?.name ?? workflowName;
          // Capture the fresh intent_history returned by the backend
          freshHistory = result?.intent_history ?? null;
          if (freshHistory) {
            setAccumulatedIntentHistory(freshHistory);
          }
        }

        // Notify parent to update dagDefinition, workflow name, and intent_history
        onDagRefined(updatedDag, updatedName, freshHistory);

        const agentMessage: RefinementMessage = {
          id: (Date.now() + 1).toString(),
          text: isEdit
            ? `✅ Workflow saved: "${refinementText}"`
            : `Workflow updated: "${refinementText}"`,
          timestamp: new Date(),
          type: "agent",
        };
        setChatHistory((prev) => [...prev, agentMessage]);
      } catch (e: any) {
        const errMsg = e?.message ?? "Failed to refine workflow. Please try again.";
        const errorMessage: RefinementMessage = {
          id: (Date.now() + 1).toString(),
          text: `⚠️ ${errMsg}`,
          timestamp: new Date(),
          type: "agent",
        };
        setChatHistory((prev) => [...prev, errorMessage]);
      } finally {
        setIsRefining(false);
      }
    } else if (onIntentChange) {
      // Fallback: just propagate the text (no API call)
      onIntentChange(refinementText);
    }
  };

  const handleSaveName = async (newName: string) => {
    if (!onWorkflowNameSave || !newName.trim() || isSavingName) return;
    setIsSavingName(true);
    try {
      await onWorkflowNameSave(newName.trim());
    } finally {
      setIsSavingName(false);
    }
  };

  const vizEdges = useMemo(
    () =>
      showDataMappings
        ? edges
        : edges.filter((e) => !(e.data as any)?.isDataFlow),
    [edges, showDataMappings],
  );

  const workflowEnvelope = useMemo(
    () => ({ dag_definition: dagDefinition }),
    [dagDefinition],
  );

  useEffect(() => {
    if (!dagDefinition?.steps?.length) return;
    (async () => {
      const { nodes: initialNodes, edges: initialEdges } =
        await loadWorkflowNodesWithTriggers(
          workflowEnvelope,
          "default",
          { type: MarkerType.ArrowClosed },
          false,
        );
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(initialNodes, initialEdges);
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    })();
  }, [workflowEnvelope, dagDefinition, setNodes, setEdges]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        height: "calc(100vh - 200px)",
        maxHeight: "720px",
        overflow: "hidden",
        boxSizing: "border-box",
        bgcolor: "#f8fafc",
      }}
    >
      {/* Main content row */}
      <Box sx={{ display: "flex", flexDirection: "row", flex: 1, minHeight: 0, width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
      {/* Left Panel: Persistent Refinement Log */}
      <Box
        sx={{
          width: "30%",
          flexShrink: 0,
          flexGrow: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid #e2e8f0",
          height: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0" }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: "#334155", textAlign: "center" }}
          >
            {isEdit ? "Update Workflow" : "Workflow Refinement"}
          </Typography>
          {isEdit && (
            <Typography
              variant="caption"
              sx={{ display: "block", textAlign: "center", color: "#64748B", mt: 0.5 }}
            >
              Describe your changes below and click <strong>Update</strong> — the workflow is saved immediately.
            </Typography>
          )}
        </Box>

        {/* Scrollable Chat Area */}
        <Box
          ref={scrollRef}
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#cbd5e1",
              borderRadius: "10px",
            },
          }}
        >
          {chatHistory.map((msg) => (
            <Box
              key={msg.id}
              sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
            >
              <Box
                sx={{
                  bgcolor: msg.type === "user" ? "#e0e7ef" : "#ffffff",
                  color: "#334155",
                  borderRadius: "18px 18px 18px 4px",
                  px: 2,
                  py: 1.5,
                  boxShadow: "0 2px 8px rgba(100,116,139,0.07)",
                  border: "1px solid #cbd5e1",
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "#cbd5e1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PersonOutlineIcon sx={{ color: "#64748B", fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748B", display: "block", mb: 0.5 }}
                  >
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      wordBreak: "break-word",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {msg.text}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}

          {/* Thinking indicator — shown while a refinement is in progress */}
          {isRefining && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box
                sx={{
                  bgcolor: "#ffffff",
                  color: "#334155",
                  borderRadius: "18px 18px 18px 4px",
                  px: 2,
                  py: 1.5,
                  boxShadow: "0 2px 8px rgba(100,116,139,0.07)",
                  border: "1px solid #cbd5e1",
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "#e0e7ef",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CircularProgress size={16} sx={{ color: "#4F6AF0" }} />
                </Box>
                <Typography variant="body2" sx={{ color: "#64748B", fontStyle: "italic" }}>
                  Updating workflow…
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Left Panel: Input Area */}
        <Box sx={{ p: 2, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
          {/* Indeterminate progress bar — visible while LLM call is in flight */}
          {isRefining && (
            <LinearProgress
              variant="indeterminate"
              sx={{
                mb: 1.5,
                borderRadius: 99,
                height: 3,
                bgcolor: "rgba(79,106,240,0.12)",
                "& .MuiLinearProgress-bar": { bgcolor: "#4F6AF0" },
              }}
            />
          )}
          <TextField
            fullWidth
            label={isEdit ? "What should change?" : "Workflow Refinement"}
            placeholder={
              isEdit
                ? "E.g. 'Add a Slack notification step at the end'"
                : "Optional: suggest a change to the workflow"
            }
            variant="filled"
            multiline
            minRows={3}
            maxRows={6}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !isEdit) {
                e.preventDefault();
                handleSendRefinement();
              }
            }}
            InputLabelProps={{
              shrink: true,
              sx: {
                color: "#8c7d70",
                fontWeight: 500,
                transform: "translate(12px, 10px) scale(0.75)",
                "&.Mui-focused": { color: "#bc8e5c" },
              },
            }}
            InputProps={{
              disableUnderline: true,
              ...(!isEdit && {
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ alignSelf: "flex-end", mb: 1, alignItems: "center" }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleSendRefinement}
                      disabled={!inputValue || !inputValue.trim() || isRefining}
                      sx={{
                        position: "absolute",
                        bottom: "12px",
                        right: "12px",
                        minWidth: "36px",
                        width: "36px",
                        height: "36px",
                        borderRadius: "45%",
                        padding: 0,
                        bgcolor: "#bc8e5c",
                        boxShadow: "0 4px 12px rgba(188, 142, 92, 0.2)",
                        "&:hover": { bgcolor: "#a37a4f" },
                        "&.Mui-disabled": { bgcolor: "#efeae6", color: "#cbd5e1" },
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isRefining ? (
                        <CircularProgress size={18} sx={{ color: "white" }} />
                      ) : (
                        <ArrowUpwardIcon sx={{ fontSize: 22 }} />
                      )}
                    </Button>
                  </InputAdornment>
                ),
              }),
              sx: {
                borderRadius: "12px",
                bgcolor: "#f7f3f0",
                padding: "24px 12px 8px 12px",
                border: "1px solid transparent",
                "&:hover": { bgcolor: "#f2ede9" },
                "&.Mui-focused": { border: "1px solid #bc8e5c" },
                "& .MuiInputBase-input": { paddingTop: "12px" },
              },
            }}
          />
          {/* Edit mode: explicit full-width Update button so the action is unambiguous */}
          {isEdit && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleSendRefinement}
              disabled={!inputValue.trim() || isRefining}
              startIcon={
                isRefining ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : undefined
              }
              sx={{
                mt: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                py: 1.2,
                bgcolor: "#bc8e5c",
                boxShadow: "0 4px 12px rgba(188, 142, 92, 0.2)",
                "&:hover": { bgcolor: "#a37a4f" },
                "&.Mui-disabled": { bgcolor: "#efeae6", color: "#cbd5e1" },
                textTransform: "none",
                fontSize: "0.95rem",
              }}
            >
              {isRefining ? "Updating…" : "Update Workflow"}
            </Button>
          )}
        </Box>
      </Box>

      {/* Right Panel: Workflow Content */}
      <Box sx={{ width: "70%", flexShrink: 0, flexGrow: 0, minWidth: 0, boxSizing: "border-box", p: 3, overflowY: "auto", overflowX: "hidden", minHeight: 0 }}>
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2,
              mb: 3,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1,
              minHeight: 36,
            }}
          >
            {isEditingName ? (
              /* Editing state: text field, saves on Enter or blur */
              <TextField
                inputRef={nameInputRef}
                fullWidth
                value={nameInputValue}
                onChange={(e) => setNameInputValue(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const trimmed = nameInputValue.trim();
                    if (!trimmed) return;
                    onWorkflowNameChange?.(trimmed);
                    setIsEditingName(false);
                    if (isEdit && onWorkflowNameSave) {
                      await handleSaveName(trimmed);
                    }
                  } else if (e.key === "Escape") {
                    // Cancel — revert to current committed name
                    setNameInputValue(workflowName);
                    setIsEditingName(false);
                  }
                }}
                onBlur={async () => {
                  const trimmed = nameInputValue.trim();
                  if (trimmed && trimmed !== workflowName) {
                    onWorkflowNameChange?.(trimmed);
                    if (isEdit && onWorkflowNameSave) {
                      await handleSaveName(trimmed);
                    }
                  } else {
                    setNameInputValue(workflowName);
                  }
                  setIsEditingName(false);
                }}
                autoFocus
                variant="standard"
                placeholder="Workflow name"
                InputProps={{ disableUnderline: false }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    color: "#1e293b",
                    py: 0.5,
                  },
                }}
              />
            ) : (
              /* Display state: static label + pencil icon */
              <>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "#1e293b", flex: 1, fontSize: "1.1rem" }}
                >
                  {workflowName || "Untitled Workflow"}
                </Typography>
                {isSavingName ? (
                  <CircularProgress size={18} sx={{ color: "#bc8e5c", flexShrink: 0 }} />
                ) : (
                  <Tooltip title="Rename workflow">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNameInputValue(workflowName);
                        setIsEditingName(true);
                      }}
                      sx={{ color: "#94a3b8", "&:hover": { color: "#bc8e5c" }, flexShrink: 0 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 3,
              px: 3,
              py: 2,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              height: "450px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 700, color: "#64748B" }}
            >
              Workflow Visualization
            </Typography>
            <ReactFlow
              nodes={nodes}
              edges={vizEdges}
              onNodesChange={onNodesChange as OnNodesChange}
              onEdgesChange={onEdgesChange as OnEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              style={{ width: "100%", height: "100%" }}
            >
              <Controls />
              <Background color="#f8fafc" gap={16} />
              <Panel position="top-left">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showDataMappings}
                        onChange={(e) => setShowDataMappings(e.target.checked)}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#64748B",
                          fontWeight: 600,
                        }}
                      >
                        Show data mappings
                      </Typography>
                    }
                    sx={{
                      bgcolor: "white",
                      borderRadius: "8px",
                      px: 1,
                      py: 0.25,
                      border: "1px solid #e2e8f0",
                      m: 0,
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const { nodes: ln, edges: le } = getLayoutedElements(nodes, edges);
                      setNodes(ln);
                      setEdges(le);
                    }}
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#64748B",
                      borderColor: "#e2e8f0",
                      bgcolor: "white",
                      borderRadius: "8px",
                      px: 1.5,
                      py: 0.25,
                      minWidth: 0,
                      textTransform: "none",
                      "&:hover": { borderColor: "#bc8e5c", color: "#bc8e5c", bgcolor: "white" },
                    }}
                  >
                    Auto Layout
                  </Button>
                </Box>
              </Panel>
            </ReactFlow>
          </Paper>

          {/* <Accordion
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight={700} color="#64748B">
                DAG Definition (JSON)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box
                component="pre"
                sx={{
                  fontSize: "0.75rem",
                  maxHeight: 300,
                  maxWidth: "100%",
                  overflowX: "auto",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  bgcolor: "#1e293b",
                  color: "#f8fafc",
                  p: 2,
                  m: 0,
                }}
              >
                {JSON.stringify(dagDefinition, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion> */}

          {/* PLACEHOLDER warning — shown when generated steps have no agent assigned */}
          {placeholderSteps.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 2, borderRadius: 3 }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                Unassigned Agent Steps
              </AlertTitle>
              {placeholderSteps.length === 1
                ? `Step "${placeholderSteps[0]}" has no agent assigned.`
                : `${placeholderSteps.length} steps have no agent assigned: ${placeholderSteps.join(", ")}.`}{" "}
              {"Saving will be blocked until real agents are assigned."}
            </Alert>
          )}
        </Box>
      </Box>

      </Box> {/* end main content row */}

      {/* Sticky footer: always visible buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          px: 3,
          py: 2,
          borderTop: "1px solid #e2e8f0",
          bgcolor: "#f8fafc",
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          sx={{ borderRadius: 99, px: 4 }}
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant={isEdit ? "outlined" : "contained"}
          color="primary"
          onClick={onConfirm}
          disabled={isCreating || !workflowName || !workflowName.trim()}
          sx={{ borderRadius: 99, px: 5, py: 1.5, fontWeight: 700 }}
        >
          {isCreating
            ? "Saving..."
            : isEdit
              ? "Back to Dashboard"
              : "Create Workflow"}
        </Button>
      </Box>
    </Box>
  );
};

export default WorkflowRefinementEditor;
